/**
 * CEO Command Center - Profile Dropdown Component
 * 
 * Combined profile and settings dropdown menu.
 * Accessible from the header via user avatar button.
 * 
 * @module components/common/ProfileDropdown
 * @version 10.3.0
 */

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '../../utils/format';
import logger from '../../services/logger';
import type { User } from '../../types';

// ============================================
// TYPES
// ============================================

interface ProfileDropdownProps {
  user: User;
  onLogout: () => void;
  onOpenSettings: () => void;
  onOpenTestSuite?: () => void;
  className?: string;
}

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  onClick: () => void;
  danger?: boolean;
  divider?: boolean;
}

// ============================================
// COMPONENT
// ============================================

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({
  user,
  onLogout,
  onOpenSettings,
  onOpenTestSuite,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Close on escape
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);
  
  const menuItems: MenuItem[] = [
    {
      id: 'settings',
      label: 'Settings',
      icon: '⚙️',
      onClick: () => {
        onOpenSettings();
        setIsOpen(false);
      },
    },
    {
      id: 'logs',
      label: 'Download Logs',
      icon: '📋',
      onClick: () => {
        logger.downloadLogs('json');
        setIsOpen(false);
      },
    },
    {
      id: 'test',
      label: 'Test Suite',
      icon: '🧪',
      onClick: () => {
        if (onOpenTestSuite) {
          onOpenTestSuite();
        }
        setIsOpen(false);
      },
    },
    {
      id: 'help',
      label: 'Help & Support',
      icon: '❓',
      onClick: () => {
        // TODO: Open help
        setIsOpen(false);
      },
      divider: true,
    },
    {
      id: 'logout',
      label: 'Logout',
      icon: '🚪',
      onClick: () => {
        onLogout();
        setIsOpen(false);
      },
      danger: true,
    },
  ];
  
  return (
    <div ref={dropdownRef} className={cn('relative', className)}>
      {/* Trigger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-2 p-1 rounded-lg transition-colors',
          isOpen ? 'bg-gray-100' : 'hover:bg-gray-100'
        )}
      >
        <div className="w-8 h-8 bg-coral-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {user.name.charAt(0).toUpperCase()}
        </div>
      </button>
      
      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border overflow-hidden z-50 animate-fade-in">
          {/* User info header */}
          <div className="px-4 py-3 border-b bg-gray-50">
            <p className="font-medium text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-500 mt-0.5">
              Logged in {new Date(user.loginAt).toLocaleDateString()}
            </p>
          </div>
          
          {/* Menu items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <React.Fragment key={item.id}>
                {item.divider && index > 0 && (
                  <div className="my-1 border-t" />
                )}
                <button
                  onClick={item.onClick}
                  className={cn(
                    'w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors',
                    item.danger
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-gray-700 hover:bg-gray-50'
                  )}
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              </React.Fragment>
            ))}
          </div>
          
          {/* Version footer */}
          <div className="px-4 py-2 border-t bg-gray-50">
            <p className="text-xs text-gray-400">v11.0.0 • Powered by Strives</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown;
